import { Download, ExternalLink, Eye, Link2, RefreshCw, ScrollText, SearchX, Trash2 } from "lucide-react";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Button from "@/components/ui/Button";
import DataTable, { type Column } from "@/components/ui/DataTable";
import EmptyState from "@/components/ui/EmptyState";
import StatusBadge from "@/components/ui/StatusBadge";
import { ToastProvider, useToast } from "@/components/ui/Toast";
import { MANDATE_TAB_DEFS } from "@/components/feature/mandates/constants";
import StatusTabs from "@/pages/retailer/mandates/components/StatusTabs";
import DeleteMandateModal from "@/pages/retailer/mandates/components/DeleteMandateModal";
import AdminFilterBar from "./components/FilterBar";
import { DEFAULT_RANGE, formatDate, formatINR, formatTime } from "@/pages/admin/data";
import { adminService, mandateService } from "@/services/apiServices";
import type { Mandate, MandateCounts } from "@/types/mandate";
import { useMandateLiveUpdates } from "@/context/RealtimeEventsContext";

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

function todayStr(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function rangeToDates(range: string, customFrom: string, customTo: string): { dateFrom?: string; dateTo?: string } {
  const now = new Date();
  switch (range) {
    case "Today":
      return { dateFrom: todayStr(now), dateTo: todayStr(now) };
    case "Last 7 Days": {
      const from = new Date(now);
      from.setDate(from.getDate() - 7);
      return { dateFrom: todayStr(from), dateTo: todayStr(now) };
    }
    case "Last 30 Days": {
      const from = new Date(now);
      from.setDate(from.getDate() - 30);
      return { dateFrom: todayStr(from), dateTo: todayStr(now) };
    }
    case "This Month": {
      const from = new Date(now.getFullYear(), now.getMonth(), 1);
      const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { dateFrom: todayStr(from), dateTo: todayStr(to) };
    }
    case "Last 3 Months": {
      const from = new Date(now);
      from.setMonth(from.getMonth() - 3);
      return { dateFrom: todayStr(from), dateTo: todayStr(now) };
    }
    case "Custom Range":
      return { dateFrom: customFrom || undefined, dateTo: customTo || undefined };
    default:
      return {};
  }
}

function escapeCsvValue(value: unknown): string {
  const s = String(value ?? "");
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function mandateCsvRows(rows: Mandate[]): string {
  const headers = [
    "Ref No",
    "Retailer",
    "Customer",
    "Mobile",
    "Bank",
    "Auth Mode",
    "EMI Amount",
    "Status",
    "Created",
  ];
  const body = rows.map((m) => [
    m.refNo,
    m.shopName,
    m.customerName,
    m.customerMobile,
    m.bankName || m.upiId || "—",
    m.authMode,
    m.emiAmount,
    m.status,
    m.createdAt,
  ]);
  return [headers, ...body].map((row) => row.map(escapeCsvValue).join(",")).join("\r\n");
}

function downloadCsv(content: string, filename: string) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

const EMPTY_COUNTS: MandateCounts = {
  all: 0,
  created: 0,
  pendingAuth: 0,
  active: 0,
  paused: 0,
  halted: 0,
  cancelled: 0,
  completed: 0,
};

export default function AdminMandates() {
  return (
    <ToastProvider>
      <MandatesInner />
    </ToastProvider>
  );
}

function MandatesInner() {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [list, setList] = useState<Mandate[]>([]);
  const [counts, setCounts] = useState<MandateCounts>(EMPTY_COUNTS);
  const [banks, setBanks] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);

  const [retailerOptions, setRetailerOptions] = useState<{ value: string; label: string }[]>([]);

  const resolveTab = useCallback(() => {
    const s = searchParams.get("status");
    const t = searchParams.get("tab");
    if (
      s?.toUpperCase() === "PENDING_AUTH" ||
      s?.toUpperCase() === "PENDING" ||
      t?.toLowerCase() === "pending" ||
      t?.toLowerCase() === "pending auth" ||
      t?.toLowerCase() === "pending authentication"
    ) {
      return "Created";
    }
    if (t) {
      const match = MANDATE_TAB_DEFS.find((d) => d.key.toLowerCase() === t.toLowerCase());
      if (match) return match.key;
    }
    if (s) {
      const match = MANDATE_TAB_DEFS.find((d) => d.status === s || d.key.toUpperCase() === s.toUpperCase());
      if (match) return match.key;
    }
    return "All";
  }, [searchParams]);

  const [tab, setTab] = useState(resolveTab);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [range, setRange] = useState(() => searchParams.get("range") ?? DEFAULT_RANGE);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [retailer, setRetailer] = useState(() => searchParams.get("retailer") ?? "");
  const [bank, setBank] = useState("");
  const [mode, setMode] = useState("");
  const [sort, setSort] = useState("Newest First");

  const [selectedKeys, setSelectedKeys] = useState<(string | number)[]>([]);
  const [refreshingId, setRefreshingId] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Mandate | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    const param = searchParams.get("retailer");
    setRetailer(param ?? "");
    const rangeParam = searchParams.get("range");
    if (rangeParam) setRange(rangeParam);
    setTab(resolveTab());
  }, [searchParams, resolveTab]);

  useEffect(() => {
    adminService
      .getRetailers({ limit: 1000 })
      .then((res) => {
        const retailers = res?.data?.retailers ?? [];
        setRetailerOptions(
          retailers.map((r: any) => {
            const code = r.userCode && !r.userCode.includes("-") ? ` (${r.userCode})` : "";
            const owner = r.name && r.name !== r.shopName ? ` • ${r.name}` : "";
            return {
              value: r.id,
              label: `${r.shopName || r.name || "Retailer"}${code}${owner}`,
            };
          })
        );
      })
      .catch(() => setRetailerOptions([]));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [search, tab, retailer, bank, mode, range, customFrom, customTo, sort]);

  const fetchMandates = useCallback(() => {
    setLoading(true);
    const tabDef = MANDATE_TAB_DEFS.find((t) => t.key === tab);
    const { dateFrom, dateTo } = rangeToDates(range, customFrom, customTo);
    mandateService
      .getMandates({
        page,
        limit: pageSize,
        search,
        status: tabDef?.status ?? undefined,
        retailerId: retailer || undefined,
        bank: bank || undefined,
        authMode: mode || undefined,
        dateFrom,
        dateTo,
        sort,
      })
      .then((res) => {
        const data = res?.data;
        setList(data?.mandates ?? []);
        setCounts(data?.counts ?? EMPTY_COUNTS);
        setBanks(data?.banks ?? []);
        setTotal(data?.pagination?.total ?? 0);
      })
      .catch((err) => {
        toast.error(err?.message || "Failed to load mandates");
        setList([]);
      })
      .finally(() => setLoading(false));
  }, [page, pageSize, search, tab, retailer, bank, mode, range, customFrom, customTo, sort, toast]);

  useEffect(() => {
    fetchMandates();
  }, [fetchMandates]);

  // Live real-time update: automatically refetches table on webhook or rocketpay updates
  useMandateLiveUpdates(() => {
    fetchMandates();
  });

  const tabItems = useMemo(
    () =>
      MANDATE_TAB_DEFS.map((t) => ({
        key: t.key,
        label: t.label,
        value: t.status
          ? counts[
              (t.key === "Pending Auth"
                ? "pendingAuth"
                : (t.key.toLowerCase() as keyof MandateCounts))
            ] ?? 0
          : counts.all,
      })),
    [counts]
  );

  const chips = useMemo(() => {
    const c: { label: string; onRemove: () => void }[] = [];
    if (searchInput.trim()) c.push({ label: `Search: ${searchInput}`, onRemove: () => setSearchInput("") });
    if (retailer) {
      const r = retailerOptions.find((o) => o.value === retailer);
      c.push({ label: `Retailer: ${r?.label ?? retailer}`, onRemove: () => setRetailer("") });
    }
    if (bank) c.push({ label: `Bank: ${bank}`, onRemove: () => setBank("") });
    if (mode) c.push({ label: `Mode: ${mode}`, onRemove: () => setMode("") });
    if (sort !== "Newest First") c.push({ label: `Sort: ${sort}`, onRemove: () => setSort("Newest First") });
    if (range !== DEFAULT_RANGE) c.push({ label: `Range: ${range}`, onRemove: () => setRange(DEFAULT_RANGE) });
    if (range === "Custom Range" && customFrom) c.push({ label: `From: ${customFrom}`, onRemove: () => setCustomFrom("") });
    if (range === "Custom Range" && customTo) c.push({ label: `To: ${customTo}`, onRemove: () => setCustomTo("") });
    return c;
  }, [searchInput, retailer, retailerOptions, bank, mode, sort, range, customFrom, customTo]);

  function resetFilters() {
    setSearchInput("");
    setRange(DEFAULT_RANGE);
    setCustomFrom("");
    setCustomTo("");
    setRetailer("");
    setBank("");
    setMode("");
    setSort("Newest First");
  }

  function copyLink(m: Mandate) {
    const url = `${window.location.origin}${m.authLink}`;
    navigator.clipboard
      .writeText(url)
      .then(() => toast.success("Auth link copied to clipboard"))
      .catch(() => toast.error("Could not copy auth link"));
  }

  function refreshStatus(m: Mandate) {
    const idToSync = m.refNo || m.id;
    if (!idToSync) return;
    setRefreshingId(m.id);
    mandateService
      .syncWithRocketPay(idToSync)
      .then((res) => {
        const updated: Mandate | undefined = res?.data?.mandate || res?.data;
        if (updated && updated.status) {
          setList((prev) =>
            prev.map((r) =>
              r.id === m.id || r.refNo === m.refNo
                ? { ...r, ...updated, status: updated.status }
                : r
            )
          );
          toast.success(`Mandate status updated: ${updated.status.replace("_", " ")}`);
        } else {
          fetchMandates();
          toast.success(res?.message || "Status refreshed from gateway");
        }
      })
      .catch((err) => toast.error(err?.message || "Failed to refresh status from gateway"))
      .finally(() => setRefreshingId(null));
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    if (deleteTarget.status !== "CREATED") {
      toast.error("Delete is only allowed when mandate is in CREATED state");
      return;
    }
    setDeleteLoading(true);
    try {
      const res = await mandateService.delete(deleteTarget.id);
      toast.success(res?.message || "Mandate deleted");
      setDeleteOpen(false);
      setDeleteTarget(null);
      fetchMandates();
    } catch (err: any) {
      toast.error(err?.message || "Failed to delete mandate");
    } finally {
      setDeleteLoading(false);
    }
  }

  async function handleExport() {
    setExporting(true);
    try {
      const tabDef = MANDATE_TAB_DEFS.find((t) => t.key === tab);
      const { dateFrom, dateTo } = rangeToDates(range, customFrom, customTo);
      const res = await mandateService.getMandates({
        page: 1,
        limit: 10000,
        search,
        status: tabDef?.status ?? undefined,
        retailerId: retailer || undefined,
        bank: bank || undefined,
        authMode: mode || undefined,
        dateFrom,
        dateTo,
        sort,
      });
      const rows: Mandate[] = res?.data?.mandates ?? [];
      if (rows.length === 0) {
        toast.error("No mandates to export");
        return;
      }
      downloadCsv(mandateCsvRows(rows), `mandates-export-${Date.now()}.csv`);
      toast.success(`Exported ${rows.length} mandate${rows.length === 1 ? "" : "s"} successfully`);
    } catch (err: any) {
      toast.error(err?.message || "Failed to export mandates");
    } finally {
      setExporting(false);
    }
  }

  function exportSelected() {
    const rows = list.filter((r) => selectedKeys.includes(r.id));
    if (rows.length === 0) {
      toast.error("No mandates selected");
      return;
    }
    downloadCsv(mandateCsvRows(rows), `mandates-selected-${Date.now()}.csv`);
    toast.success(`Exported ${rows.length} mandate${rows.length === 1 ? "" : "s"} successfully`);
  }

  const columns: Column<Mandate>[] = [
    {
      key: "refNo",
      header: "Ref No",
      width: "140px",
      render: (row) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/admin/mandates/${row.refNo}`);
          }}
          className="text-[13px] font-medium text-primary-500 hover:text-primary-600 cursor-pointer"
        >
          {row.refNo}
        </button>
      ),
    },
    {
      key: "shopName",
      header: "Retailer",
      width: "180px",
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-ink">{row.shopName}</p>
          <p className="truncate text-xs text-ink-secondary">
            {row.retailerName && row.retailerName !== row.shopName
              ? row.retailerName
              : (!row.retailerId?.includes("-") ? row.retailerId : "")}
          </p>
        </div>
      ),
    },
    {
      key: "customerName",
      header: "Customer",
      width: "180px",
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-ink">{row.customerName}</p>
          <p className="text-xs text-ink-secondary">{row.customerMobile}</p>
        </div>
      ),
    },
    {
      key: "product",
      header: "Product",
      width: "160px",
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-ink">
            {[row.brand, row.model].filter(Boolean).join(" ") || "—"}
          </p>
          <p className="truncate text-xs text-ink-secondary">{row.category ?? "—"}</p>
        </div>
      ),
    },
    {
      key: "bankName",
      header: "Bank / UPI",
      width: "160px",
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-ink">{row.bankName || row.upiId || "—"}</p>
          <p className="text-xs text-ink-secondary">{row.accountNumber || ""}</p>
        </div>
      ),
    },
    {
      key: "amount",
      header: "EMI",
      width: "110px",
      align: "right",
      render: (row) => (
        <div className="text-right">
          <p className="text-sm font-medium text-ink">{formatINR(row.emiAmount)}</p>
          <p className="text-xs text-ink-secondary">{row.tenureMonths} months</p>
        </div>
      ),
    },
    {
      key: "authMode",
      header: "Auth Mode",
      width: "110px",
      render: (row) => <span className="whitespace-nowrap text-[13px] text-ink">{row.authMode}</span>,
    },
    {
      key: "status",
      header: "Status",
      width: "135px",
      render: (row) => {
        const isSyncing = refreshingId === row.id || refreshingId === row.refNo;
        return (
          <div className="inline-flex items-center gap-1.5">
            <StatusBadge status={row.status} />
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                refreshStatus(row);
              }}
              disabled={isSyncing}
              title="Fetch latest status from gateway"
              className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-primary-600 hover:border-primary-300 shadow-sm transition-all cursor-pointer disabled:opacity-50"
            >
              <RefreshCw className={`h-3 w-3 ${isSyncing ? "animate-spin text-primary-600" : ""}`} />
            </button>
          </div>
        );
      },
    },
    {
      key: "createdAt",
      header: "Created",
      width: "120px",
      render: (row) => (
        <div>
          <p className="whitespace-nowrap text-[13px] text-ink">{formatDate(row.createdAt)}</p>
          <p className="text-xs text-ink-secondary">{formatTime(row.createdAt)}</p>
        </div>
      ),
    },
  ];

  function rowActions(row: Mandate) {
    const resendable = row.status === "PENDING_AUTH" || row.status === "CREATED";
    const deletable = row.status === "CREATED";
    return (
      <>
        <MenuAction icon={<Eye className="h-4 w-4 text-ink-muted" />} onClick={() => navigate(`/admin/mandates/${row.refNo}`)}>
          View Details
        </MenuAction>
        {resendable && (
          <MenuAction icon={<Link2 className="h-4 w-4 text-ink-muted" />} onClick={() => copyLink(row)}>
            Copy Auth Link
          </MenuAction>
        )}
        {resendable && (
          <MenuAction
            icon={<ExternalLink className="h-4 w-4 text-ink-muted" />}
            onClick={() => window.open(row.authLink, "_blank", "noopener,noreferrer")}
          >
            Preview Link
          </MenuAction>
        )}
        <MenuAction
          icon={
            refreshingId === row.id ? (
              <RefreshCw className="h-4 w-4 animate-spin text-ink-muted" />
            ) : (
              <RefreshCw className="h-4 w-4 text-ink-muted" />
            )
          }
          onClick={() => refreshStatus(row)}
        >
          {refreshingId === row.id ? "Refreshing..." : "Refresh Status"}
        </MenuAction>
        <MenuAction icon={<ScrollText className="h-4 w-4 text-ink-muted" />} onClick={() => navigate("/admin/logs")}>
          View Logs
        </MenuAction>
        {deletable && (
          <MenuAction
            icon={<Trash2 className="h-4 w-4" />}
            danger
            onClick={() => {
              setDeleteTarget(row);
              setDeleteOpen(true);
            }}
          >
            Delete Mandate
          </MenuAction>
        )}
      </>
    );
  }

  const selectionBar = (count: number, clear: () => void) => (
    <div className="flex flex-wrap items-center justify-between gap-3 bg-primary-50 px-4 py-3">
      <p className="text-sm font-medium text-primary-600">{count} selected</p>
      <div className="flex items-center gap-2">
        <Button variant="secondary" size="sm" onClick={exportSelected}>
          Export Selected
        </Button>
        <Button variant="ghost" size="sm" onClick={clear}>
          Clear Selection
        </Button>
      </div>
    </div>
  );

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 py-6 md:px-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-ink">All Mandates</h1>
          <p className="mt-1 text-sm text-ink-secondary">Every mandate created across all retailers</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="secondary" onClick={handleExport} loading={exporting} leftIcon={<Download className="h-4 w-4" />}>
            Export
          </Button>
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-4">
        <StatusTabs tabs={tabItems} active={tab} onChange={setTab} />

        <AdminFilterBar
          search={searchInput}
          onSearch={setSearchInput}
          range={range}
          onRange={setRange}
          customFrom={customFrom}
          customTo={customTo}
          onCustomFrom={setCustomFrom}
          onCustomTo={setCustomTo}
          retailer={retailer}
          onRetailer={setRetailer}
          retailerOptions={retailerOptions}
          bank={bank}
          onBank={setBank}
          banks={banks}
          mode={mode}
          onMode={setMode}
          sort={sort}
          onSort={setSort}
          onReset={resetFilters}
          chips={chips}
        />

        {loading ? (
          <DataTable columns={columns} data={[]} rowKey={(r) => r.id} loading selectable />
        ) : list.length === 0 ? (
          <EmptyState
            icon={SearchX}
            title="No mandates found"
            description="Try adjusting your filters or search term."
            actionLabel="Reset Filters"
            onAction={resetFilters}
          />
        ) : (
          <DataTable
            columns={columns}
            data={list}
            rowKey={(r) => r.id}
            selectable
            actions={rowActions}
            onRowClick={(row) => navigate(`/admin/mandates/${row.refNo}`)}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            maxHeight="520px"
            selectedKeys={selectedKeys}
            onSelectionChange={setSelectedKeys}
            selectionBar={selectionBar}
            serverPagination={{
              page,
              pageSize,
              total,
              onPageChange: setPage,
              onPageSizeChange: (n) => {
                setPageSize(n);
                setPage(1);
              },
            }}
          />
        )}
      </div>

      <DeleteMandateModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        refNo={deleteTarget?.refNo ?? ""}
        onConfirm={confirmDelete}
        loading={deleteLoading}
      />
    </div>
  );
}

interface MenuActionProps {
  icon: ReactNode;
  children: ReactNode;
  onClick: () => void;
  danger?: boolean;
}

function MenuAction({ icon, children, onClick, danger }: MenuActionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-slate-50 cursor-pointer ${
        danger ? "text-danger" : "text-ink"
      }`}
    >
      {icon}
      {children}
    </button>
  );
}
